// Library imports
import { Avatar, Skeleton, Tooltip } from "@heroui/react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useLocation } from "react-router-dom";
import { useIsMobile } from "../../../hooks/useIsMobile";
import { useIsClient } from "../../../hooks/useClientRole";
import { NotificationBell } from "../NotificationBell";

// Icons imports
import {
  LogoutIcon,
  MenuIcon,
  CloseIcon,
  DoubleArrowIcon,
  HelpIcon,
  SettingIcon,
  PlusIcon,
} from "../../../assets/icons";

// Local imports
import { assets } from "../../../assets/images";
import { useProfileDetails } from "../../../lib/react-query/profile";
import { useProjectsList } from "../../../lib/react-query/projects";
import { capitalize, getFullName, logoutFn } from "../../../utils/utils";
import { ContactSupportModal } from "../../Support/ContactSupportModal";
import { HelpSupportOptionsModal } from "../../Support/HelpSupportOptionsModal";
import { SupportFaqModal } from "../../Support/SupportFaqModal";
import { CustomModal } from "../Modal";
import { allNavigationItems } from "./navigation";

// Check if route is active
const isActiveRoute = (pathname, routePath) => {
  return pathname?.split("/")[1] === routePath?.split("/")[1];
};

const getActiveProjectId = (pathname = "") => {
  const match = pathname.match(/^\/projects\/([^/]+)/);
  return match?.[1];
};

const getVisibleSubItems = (subItems, pathname) => {
  const hasProjectContext = !!getActiveProjectId(pathname);
  return subItems.filter((sub) => {
    if (!sub.projectScoped) return true;
    return hasProjectContext;
  });
};

const resolveSubItemPath = (subItem, pathname) => {
  if (!subItem.projectScoped) return subItem.path;

  const projectId = getActiveProjectId(pathname);
  if (!projectId) return "/projects";

  return `/projects/${projectId}${subItem.path}`;
};

// Sidebar Navigation Item Component
const SidebarNavItem = ({
  item,
  isActive,
  onClick,
  collapsed,
  location,
  navigate,
}) => {
  const Icon = item.icon;
  const visibleSubItems = getVisibleSubItems(item.subItems || [], location?.pathname);
  const hasSubItems = visibleSubItems.length > 0;
  const isSubItemActive =
    hasSubItems &&
    visibleSubItems.some((sub) => {
      const subPath = resolveSubItemPath(sub, location?.pathname);
      return sub.projectScoped
        ? location?.pathname === subPath || location?.pathname.startsWith(`${subPath}/`)
        : isActiveRoute(location?.pathname, sub.path);
    });
  const isItemActive = isActive || isSubItemActive;
  const [subMenuOpen, setSubMenuOpen] = useState(isItemActive && hasSubItems);

  useEffect(() => {
    if (isItemActive && hasSubItems) {
      setSubMenuOpen(true);
    }
  }, [hasSubItems, isItemActive]);

  const content = (
    <div>
      <div className="relative">
        <button
          onClick={onClick}
          className={`relative w-full flex items-center gap-3 px-2 py-2 text-xs font-medium transition-all duration-200 group cursor-pointer
            ${
              isItemActive
                ? "text-primary bg-primary/5"
                : "text-darkgrey hover:text-primary hover:bg-surface"
            }
            ${collapsed ? "justify-center rounded-lg" : "rounded-r-lg mr-2"}
            ${hasSubItems && !collapsed ? "pr-8" : ""}
          `}
        >
          {/* Active Indicator Strip (only when expanded) */}
          {!collapsed && isItemActive && (
            <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-r-md" />
          )}

          <Icon
            width={22}
            height={22}
            className={`transition-colors duration-200 ${
              isItemActive
                ? "text-primary"
                : "text-darkgrey group-hover:text-darkgrey"
            }`}
          />

          {!collapsed && (
            <span
              className={`flex-1 text-left ${isItemActive ? "font-semibold" : "font-medium"}`}
            >
              {item.label}
              {item.badge && (
                <span className="ml-2 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                  {item.badge}
                </span>
              )}
            </span>
          )}
        </button>
        {!collapsed && hasSubItems && (
          <button
            type="button"
            aria-label={subMenuOpen ? `Collapse ${item.label} submenu` : `Expand ${item.label} submenu`}
            aria-expanded={subMenuOpen}
            className="absolute right-1 top-1/2 -translate-y-1/2 p-1 hover:bg-surface-active rounded transition-colors"
            onClick={(e) => {
              e.stopPropagation();
              setSubMenuOpen(prev => !prev);
            }}
          >
            <svg
              width={14}
              height={14}
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth={2}
              strokeLinecap="round"
              strokeLinejoin="round"
              className={`transition-transform duration-200 text-darkgrey ${
                subMenuOpen ? "rotate-180" : ""
              }`}
            >
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </button>
        )}
      </div>

      {/* Collapsible Sub-items */}
      {!collapsed && hasSubItems && (
        <div
          className="ml-7 space-y-0.5 overflow-hidden transition-all duration-200"
          style={{
            maxHeight: subMenuOpen ? `${visibleSubItems.length * 40}px` : "0px",
            opacity: subMenuOpen ? 1 : 0,
            marginTop: subMenuOpen ? "2px" : "0px",
          }}
        >
          {visibleSubItems.map((sub) => {
            const SubIcon = sub.icon;
            const subPath = resolveSubItemPath(sub, location?.pathname);
            const subActive = sub.projectScoped
              ? location?.pathname === subPath || location?.pathname.startsWith(`${subPath}/`)
              : isActiveRoute(location?.pathname, sub.path);
            return (
              <button
                key={sub.path}
                onClick={() => navigate?.(subPath)}
                className={`w-full flex items-center gap-2.5 pl-3 pr-2 py-1.5 text-xs font-medium rounded-r-lg mr-2 transition-all duration-200 cursor-pointer
                  ${
                    subActive
                      ? "text-primary bg-primary/5"
                      : "text-darkgrey hover:text-grey hover:bg-surface"
                  }
                `}
              >
                <SubIcon
                  width={16}
                  height={16}
                  className={`transition-colors duration-200 ${
                    subActive ? "text-primary" : "text-darkgrey"
                  }`}
                />
                <span>{sub.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );

  if (collapsed) {
    return (
      <Tooltip content={item.label} placement="right" delay={0} closeDelay={0}>
        {content}
      </Tooltip>
    );
  }

  return content;
};

export const DashboardLayout = ({ children }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector((state) => state?.user?.user);
  const hasAccessToken = !!user?.token?.access;
  const { data: profileDetails, isLoading: profileLoading } =
    useProfileDetails(hasAccessToken);
  const { data: projectsData, isLoading: projectsSidebarLoading } =
    useProjectsList({ ordering: "-updated_at", page_size: 3 }, hasAccessToken);
  const recentProjects = projectsData?.items || [];
  const [openModal, setOpenModal] = useState(false);
  const [supportModal, setSupportModal] = useState(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const isMobile = useIsMobile();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    localStorage.getItem("sidebarCollapsed") === "true",
  );

  const { isClient: isClientModeActive } = useIsClient();
  const navigationItems = isClientModeActive
    ? allNavigationItems
        .filter((item) => !item.hiddenForClient)
        .map((item) => ({
          ...item,
          subItems: item.subItems?.filter((subItem) => !subItem.hiddenForClient),
        }))
    : allNavigationItems;

  // Close mobile menu on desktop
  useEffect(() => {
    if (!isMobile) {
      setIsMobileMenuOpen(false);
    }
  }, [isMobile]);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  const toggleSidebar = (event) => {
    event.stopPropagation();
    const newState = !sidebarCollapsed;
    setSidebarCollapsed(newState);
    localStorage.setItem("sidebarCollapsed", String(newState));
  };

  const handleLogout = async () => {
    await logoutFn();
    navigate("/login");
  };

  const openSupportOptions = () => setSupportModal("options");
  const closeSupportModal = () => setSupportModal(null);
  const handleSupportOption = (option) => setSupportModal(option);

  const userDisplayName = user ? capitalize(getFullName(user)) : "User";
  const userEmail = user?.email || profileDetails?.email || "";
  const userAvatar =
    profileDetails?.profile_image || user?.profile_image || null;

  // Sidebar Content Component (collapsed version for both mobile and desktop collapsed)
  const CollapsedSidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo Section */}
      <div
        className="flex items-center justify-center px-4 py-5 flex-shrink-0 relative"
      >
        <button
          type="button"
          onClick={() => navigate("/")}
          aria-label="Go to Home"
          className="relative rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          <img
            src={assets.logo}
            alt="DealNest"
            className="w-9 h-9 object-contain"
          />
          {isClientModeActive && (
            <span className="absolute -top-3.5 -right-2 inline-flex items-center rounded-full bg-primary px-2 py-1 text-[9px] font-bold uppercase leading-none tracking-[0.16em] text-white shadow-sm">
              CLIENT
            </span>
          )}
        </button>
        {/* Expand Button - Desktop only, positioned outside */}
        {!isMobile && (
          <button
            onClick={toggleSidebar}
            className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 p-1 rounded-full bg-white shadow-md border border-surface-border hover:bg-surface transition-colors z-overlay group"
            aria-label="Expand sidebar" title="Expand sidebar"
          >
            <DoubleArrowIcon
              width={12}
              height={12}
              className="text-darkgrey group-hover:text-primary rotate-180 transition-colors"
            />
          </button>
        )}
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 overflow-y-auto px-2 py-4 pb-6 space-y-6 min-h-0 no-scrollbar">
        {/* Navigation - Icons only, centered */}
        <nav className="space-y-1 flex-shrink-0">
          {navigationItems.map((item) => {
            const targetPath = item.path;
            return (
              <SidebarNavItem
                key={item.path}
                item={item}
                isActive={isActiveRoute(location.pathname, item.path)}
                onClick={() => navigate(targetPath)}
                collapsed={true}
                location={location}
                navigate={navigate}
              />
            );
          })}
        </nav>
      </div>

      {/* Bottom Section */}
      <div className="px-2 py-4 flex-shrink-0 bg-white space-y-3">
        <div className="border-t border-surface-border" />
        {!isClientModeActive && (
          <Tooltip
            content="Account Settings"
            placement="right"
            delay={0}
            closeDelay={0}
          >
            <button
              onClick={() => navigate("/account-settings")}
              className={`w-full flex items-center justify-center px-3 py-3 rounded-lg transition-all duration-200
                ${
                  isActiveRoute(location.pathname, "/account-settings")
                    ? "text-primary bg-primary/5"
                    : "text-darkgrey hover:bg-surface hover:text-darkgrey"
                }`}
            >
              <SettingIcon width={20} height={20} />
            </button>
          </Tooltip>
        )}
        <Tooltip
          content="Help & Support"
          placement="right"
          delay={0}
          closeDelay={0}
        >
          <button
            type="button"
            onClick={openSupportOptions}
            aria-label="Help & Support"
            className={`w-full flex items-center justify-center px-3 py-3 rounded-lg transition-all duration-200 ${
              isActiveRoute(location.pathname, "/support")
                ? "text-primary bg-primary/5"
                : "text-darkgrey hover:bg-surface hover:text-darkgrey"
            }`}
          >
            <HelpIcon width={20} height={20} />
          </button>
        </Tooltip>
        {/* Notification Bell - Collapsed */}
        <NotificationBell collapsed />
        <Tooltip content="Sign out" placement="right" delay={0} closeDelay={0}>
          <button
            onClick={() => setOpenModal(true)}
            className="w-full flex items-center justify-center px-3 py-3 text-darkgrey hover:bg-danger/10 hover:text-danger rounded-lg transition-all duration-200"
          >
            <LogoutIcon width={20} height={20} />
          </button>
        </Tooltip>
        {/* User Avatar - Centered */}
        <div className="border-t border-surface-border" />
        <div className="flex justify-center flex-shrink-0 pt-1">
          {profileLoading ? (
            <Skeleton className="w-10 h-10 rounded-full" />
          ) : (
            <Tooltip
              content={
                <div className="px-1 py-1">
                  <p className="font-semibold text-xs">{userDisplayName}</p>
                  <p className="text-[10px] text-darkgrey">{userEmail}</p>
                </div>
              }
              placement="right"
              delay={0}
              closeDelay={0}
            >
              <button
                type="button"
                className="relative group rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
                onClick={() => navigate("/profile/profile-details")}
                aria-label={`View profile for ${userDisplayName}`}
              >
                <Avatar
                  src={userAvatar}
                  name={userDisplayName}
                  className="w-10 h-10 transition-transform group-hover:scale-105"
                  classNames={{
                    base: "bg-primary/10 ring-2 ring-transparent group-hover:ring-primary/20",
                    icon: "text-primary",
                  }}
                />
              </button>
            </Tooltip>
          )}
        </div>
      </div>
    </div>
  );

  // Full Sidebar Content (desktop expanded only)
  const FullSidebarContent = () => (
    <div className="flex flex-col h-full">
      {/* Logo Section */}
      <div
        className="flex items-center gap-3 px-6 py-4 flex-shrink-0 relative"
      >
        <button
          type="button"
          onClick={() => navigate("/")}
          aria-label="Go to dashboard"
          className="flex items-center gap-3 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
        >
          <img
            src={assets.logo}
            alt="DealNest"
            className="w-8 h-8 object-contain flex-shrink-0"
          />
          <span className="flex items-baseline gap-1.5">
            <span className="text-xl font-bold text-primary tracking-tight">
              DealNest
            </span>
            {isClientModeActive && (
              <span className="relative -top-2 inline-flex items-center rounded-full bg-primary px-2 py-1 text-[9px] font-bold uppercase leading-none tracking-[0.16em] text-white shadow-sm">
                CLIENT
              </span>
            )}
          </span>
        </button>
        {/* Collapse Button - Desktop only */}
        <button
          onClick={toggleSidebar}
          className="hidden lg:flex items-center justify-center absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 p-1.5 rounded-full bg-white shadow-md border border-surface-border hover:bg-surface transition-colors z-overlay group"
          aria-label="Collapse sidebar" title="Collapse sidebar"
        >
          <DoubleArrowIcon
            width={12}
            height={12}
            className="text-darkgrey group-hover:text-primary transition-colors"
          />
        </button>
      </div>

        {/* Navigation */}
        <div className="flex-shrink-0 px-4 pt-2 pb-1 space-y-1">
         <div className="pb-2">
          <p className="px-3 text-xs font-semibold text-darkgrey uppercase tracking-widest mb-3">
            Menu
          </p>
          <nav className="space-y-1 flex-shrink-0">
            {navigationItems.map((item) => {
              const targetPath =
                item.path === "/draft-flow" ? "/draft-flow" : item.path;
              return (
                <SidebarNavItem
                  key={item.path}
                  item={item}
                  isActive={isActiveRoute(location.pathname, item.path)}
                  onClick={() => navigate(targetPath)}
                  collapsed={false}
                  location={location}
                  navigate={navigate}
                />
              );
            })}
          </nav>
         </div>
        </div>

      {/* Scrollable Recent Projects */}
      <div className="flex-1 overflow-y-auto px-4 pb-4 min-h-0 no-scrollbar">
        <div>
          <div className="flex items-center justify-between px-3 mb-2">
            <p className="text-xs font-semibold text-darkgrey uppercase tracking-widest">
              Recent Projects
            </p>
            {!isClientModeActive && (
              <button
                onClick={() => navigate("/projects?prompt_project_create=1")}
                className="p-1 rounded-md text-darkgrey hover:text-primary hover:bg-primary/5 transition-colors cursor-pointer"
                title="Create project"
              >
                <PlusIcon width={14} height={14} />
              </button>
            )}
          </div>

          {projectsSidebarLoading ? (
            <div className="space-y-1 px-1">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="flex items-center gap-2.5 px-2 py-2">
                  <Skeleton className="w-5 h-5 rounded flex-shrink-0" />
                  <Skeleton className="h-3 w-full rounded" />
                </div>
              ))}
            </div>
          ) : recentProjects.length === 0 ? (
            // Slim single-line state: the page canvas owns the primary empty state + CTA.
            // The header "+" button above already covers project creation.
            <p className="px-3 py-2 text-xs text-darkgrey">No projects yet</p>
          ) : (
            <div className="space-y-1">
              {recentProjects.map((project) => {
                const isProjectActive = location.pathname.startsWith(
                  `/projects/${project.id}`,
                );
                const isPendingInvite = project.user_membership_status === 'PENDING';
                return (
                  <button
                    key={project.id}
                    onClick={() =>
                      !isPendingInvite && navigate(`/projects/${project.id}/tasks`)
                    }
                    disabled={isPendingInvite}
                    className={`relative w-full flex items-center gap-3 px-2 py-2 text-xs font-medium transition-all duration-200 rounded-r-lg mr-2 truncate
                      ${isPendingInvite
                        ? "cursor-not-allowed text-surface-disabled"
                        : isProjectActive
                          ? "text-primary bg-primary/5 cursor-pointer group"
                          : "text-darkgrey hover:text-primary hover:bg-surface cursor-pointer group"
                      }
                    `}
                    title={isPendingInvite ? `${project.title} — pending invitation` : project.title}
                  >
                    {isProjectActive && !isPendingInvite && (
                      <div className="absolute left-0 top-0 bottom-0 w-1 bg-primary rounded-r-md" />
                    )}
                    <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${isPendingInvite ? "bg-surface-disabled" : "bg-primary"}`} />
                    <span className={`flex-1 text-left truncate ${isProjectActive && !isPendingInvite ? "font-semibold" : "font-medium"} ${isPendingInvite ? "line-through" : ""}`}>
                      {project.title}
                    </span>
                  </button>
                );
              })}
              <button
                onClick={() => navigate("/projects")}
                className="relative w-full flex items-center gap-3 px-2 py-2 text-xs font-medium text-darkgrey hover:text-primary hover:bg-surface rounded-r-lg mr-2 transition-all duration-200 cursor-pointer"
              >
                <span className="flex-1 text-left">{isClientModeActive ? "See all projects" : "View all projects"}</span>
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Section */}
      <div className="px-4 py-2 flex-shrink-0 bg-white">
        <div className="border-t border-surface-border mb-1" />
        <div className="space-y-0">
          {!isClientModeActive && (
            <button
              onClick={() => navigate("/account-settings")}
              className={`w-full flex items-center gap-3 px-2 py-1.5 text-sm font-medium rounded-lg transition-colors group
                ${
                  isActiveRoute(location.pathname, "/account-settings")
                    ? "text-primary bg-primary/5"
                    : "text-darkgrey hover:bg-surface hover:text-grey"
                }`}
            >
              <SettingIcon
                width={18}
                height={18}
                className={`transition-colors ${
                  isActiveRoute(location.pathname, "/account-settings")
                    ? "text-primary"
                    : "text-darkgrey group-hover:text-darkgrey"
                }`}
              />
              <span>Account Settings</span>
            </button>
          )}
          <button
            type="button"
            onClick={openSupportOptions}
            className={`w-full flex items-center gap-3 px-2 py-1.5 text-sm font-medium rounded-lg transition-colors group ${
              isActiveRoute(location.pathname, "/support")
                ? "text-primary bg-primary/5"
                : "text-darkgrey hover:bg-surface hover:text-grey"
            }`}
          >
            <HelpIcon
              width={18}
              height={18}
              className="transition-colors"
            />
            <span>Help & Support</span>
          </button>
          {/* Notification Bell - Expanded */}
          <NotificationBell />
          <button
            onClick={() => setOpenModal(true)}
            className="w-full flex items-center gap-3 px-2 py-1.5 text-sm font-medium text-darkgrey hover:bg-danger/10 hover:text-danger rounded-lg transition-colors group"
          >
            <LogoutIcon
              width={18}
              height={18}
              className="text-darkgrey group-hover:text-danger transition-colors"
            />
            <span>Sign out</span>
          </button>
        </div>
        {/* User Profile Card */}
        <div className="border-t border-surface-border my-1" />
        <div className="flex-shrink-0">
          <button
            type="button"
            className="group w-full flex items-center gap-2.5 px-2 py-1.5 rounded-lg hover:bg-surface cursor-pointer transition-colors text-left"
            onClick={() => navigate("/profile/profile-details")}
            aria-label={`View profile for ${userDisplayName}`}
          >
            {profileLoading ? (
              <Skeleton className="w-8 h-8 rounded-full flex-shrink-0" />
            ) : (
              <Avatar
                src={userAvatar}
                name={userDisplayName}
                className="w-8 h-8 flex-shrink-0"
                classNames={{
                  base: "bg-primary/10",
                  icon: "text-primary",
                }}
              />
            )}
            <div className="min-w-0 flex-1 overflow-hidden">
              {profileLoading ? (
                <>
                  <Skeleton className="w-24 h-3.5 rounded mb-1" />
                  <Skeleton className="w-32 h-3 rounded" />
                </>
              ) : (
                <>
                  <p className="text-sm font-semibold text-primary truncate group-hover:text-primary transition-colors">
                    {userDisplayName}
                  </p>
                  <p className="text-xs text-darkgrey truncate">{userEmail}</p>
                </>
              )}
            </div>
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div
      className="flex h-screen bg-bggrey font-sans"
      data-support-screenshot-target
    >
      {/* Desktop Sidebar */}
      <aside
        className={`hidden lg:flex flex-col bg-white border-r border-surface-border h-full flex-shrink-0 z-30 transition-all duration-300 shadow-sm ${
          sidebarCollapsed ? "w-20" : "w-64"
        }`}
      >
        {sidebarCollapsed ? (
          <CollapsedSidebarContent />
        ) : (
          <FullSidebarContent />
        )}
      </aside>

      {/* Mobile Menu Button */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-sticky bg-white border-b border-surface-border shadow-sm">
        <div className="flex items-center justify-between px-4 py-3">
          <button
            type="button"
            className="flex items-center gap-2 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/40"
            onClick={() => navigate("/")}
            aria-label="Go to dashboard"
          >
            <img
              src={assets.logo}
              alt="DealNest"
              className="w-7 h-7 object-contain"
            />
            <span className="text-base font-bold text-primary">DealNest</span>
          </button>
          <button
            aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"} onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="p-2 rounded-lg hover:bg-surface-hover transition-colors bg-surface"
          >
            {isMobileMenuOpen ? (
              <CloseIcon width={20} height={20} className="text-darkgrey" />
            ) : (
              <MenuIcon width={20} height={20} className="text-darkgrey" />
            )}
          </button>
        </div>
      </div>

      {/* Mobile Sidebar Drawer - Full style */}
      {isMobileMenuOpen && (
        <>
          {/* Overlay */}
          <div
            className="lg:hidden fixed inset-0 bg-black/20 backdrop-blur-sm z-overlay transition-opacity"
            onClick={() => setIsMobileMenuOpen(false)}
          />
          {/* Drawer - Full style */}
          <aside className="lg:hidden fixed top-0 left-0 bottom-0 w-64 bg-white z-drawer shadow-2xl transform transition-transform duration-300">
            <FullSidebarContent />
          </aside>
        </>
      )}

      {/* Main Content Area */}
      <main
        className={`flex-1 min-h-0 pt-14 lg:pt-0 flex flex-col ${
          location.pathname.startsWith("/draft-flow")
            ? "overflow-hidden"
            : "overflow-auto"
        }`}
      >
        <div
          className={`w-full max-w-[1440px] mx-auto flex flex-col ${
            location.pathname.startsWith("/draft-flow")
              ? "flex-1 min-h-0 p-0"
              : "flex-1 min-h-0 p-4 sm:p-6 lg:p-8"
          }`}
        >
          {children}
        </div>
      </main>

      <HelpSupportOptionsModal
        open={supportModal === "options"}
        onClose={closeSupportModal}
        onSelect={handleSupportOption}
      />
      <SupportFaqModal
        open={supportModal === "faq"}
        onClose={closeSupportModal}
      />
      <ContactSupportModal
        open={["SUPPORT_REQUEST", "FEEDBACK"].includes(supportModal)}
        onClose={closeSupportModal}
        kind={supportModal || "SUPPORT_REQUEST"}
        context={{ pagePath: location.pathname }}
      />

      {/* Logout Confirmation Modal */}
      <CustomModal
        title="Sign out"
        open={openModal}
        close={() => setOpenModal(false)}
        primaryButtonClick={handleLogout}
        secondaryButtonClick={() => setOpenModal(false)}
        primaryButtonText="Sign out"
        primaryButtonColor="danger"
      >
        <div className="font-sans pb-3">
          <p className="text-sm text-darkgrey mb-3">
            Are you sure you want to sign out?
          </p>
          <p className="text-xs text-darkgrey">
            You&apos;ll be redirected to the login page and any unsaved changes will be lost.
          </p>
        </div>
      </CustomModal>
    </div>
  );
};
